import { useEffect, useState, useCallback, useMemo } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CategoryPills } from "@/components/CategoryPills";
import { ServiceCard } from "@/components/ServiceCard";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, MapPin, Users, Loader2, Sparkles, List, Map as MapIcon, Euro, CalendarDays, ArrowDownUp } from "lucide-react";
import { OffersMap } from "@/components/OffersMap";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { CITY_COORDS, dist } from "@/lib/geo";

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const HERO = "https://images.unsplash.com/photo-1513625047154-f390b1b0df20?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [active, setActive] = useState("alle");
  const [q, setQ] = useState("");
  const [guests, setGuests] = useState("");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [price, setPrice] = useState([0, 2000]);
  const [priceDraft, setPriceDraft] = useState([0, 2000]);
  const [date, setDate] = useState("");
  const [sort, setSort] = useState("recommended");
  const [userLoc, setUserLoc] = useState(null);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (active !== "alle") params.category = active;
    if (q) params.q = q;
    if (guests) params.guests = guests;
    if (price[0] > 0) params.min_price = price[0];
    if (price[1] < 2000) params.max_price = price[1];
    if (date) params.date = date;
    const { data } = await api.get("/services", { params });
    setServices(data);
    setLoading(false);
  }, [active, q, guests, price, date]);

  useEffect(() => { load(); }, [active, price, date]);

  const requestLocation = () => {
    if (!navigator.geolocation) { toast.error("Standort wird nicht unterstützt."); setSort("recommended"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserLoc([pos.coords.latitude, pos.coords.longitude]); toast.success("Nach Entfernung sortiert."); },
      () => { toast.error("Standort nicht verfügbar – Sortierung zurückgesetzt."); setSort("recommended"); }
    );
  };
  const onSort = (v) => { setSort(v); if (v === "distance" && !userLoc) requestLocation(); };

  const displayed = useMemo(() => {
    const arr = [...services];
    if (sort === "price_asc") arr.sort((a, b) => a.base_price - b.base_price);
    else if (sort === "price_desc") arr.sort((a, b) => b.base_price - a.base_price);
    else if (sort === "rating") arr.sort((a, b) => b.rating - a.rating);
    else if (sort === "distance" && userLoc) arr.sort((a, b) => {
      const ca = CITY_COORDS[a.city], cb = CITY_COORDS[b.city];
      return (ca ? dist(userLoc, ca) : Infinity) - (cb ? dist(userLoc, cb) : Infinity);
    });
    return arr;
  }, [services, sort, userLoc]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/90 via-emerald-900/80 to-emerald-800/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-20 pb-28">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-emerald-50 text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Über 500 Partner in ganz Deutschland
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight max-w-3xl leading-[1.05]">
            Dein Event-Catering & Entertainment in Minuten gebucht.
          </h1>
          <p className="mt-5 text-lg text-emerald-100/90 max-w-xl">
            Pizzawagen, DJ, Cocktailbar & mehr – für Hochzeit, Geburtstag, Firmenfeier oder Jubiläum. Transparent, schnell, direkt online buchbar.
          </p>

          <div className="mt-9 bg-white rounded-2xl shadow-2xl p-3 flex flex-col sm:flex-row gap-2 max-w-3xl">
            <div className="flex items-center gap-2 flex-1 px-3">
              <Search className="w-5 h-5 text-slate-400" />
              <Input data-testid="hero-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Pizzawagen, DJ, Stadt…" className="border-0 shadow-none focus-visible:ring-0 px-0" />
            </div>
            <div className="flex items-center gap-2 px-3 border-t sm:border-t-0 sm:border-l border-slate-200 sm:w-40">
              <Users className="w-5 h-5 text-slate-400" />
              <Input data-testid="hero-guests" type="number" value={guests} onChange={(e) => setGuests(e.target.value)}
                placeholder="Gäste" className="border-0 shadow-none focus-visible:ring-0 px-0" />
            </div>
            <Button onClick={load} data-testid="hero-search-btn" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl h-12 px-8 font-semibold">
              Suchen
            </Button>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-8">
          <CategoryPills categories={categories} active={active} onChange={setActive} />
        </div>

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            {active === "alle" ? "Alle Angebote" : categories.find((c) => c.slug === active)?.name}
          </h2>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <span className="text-sm text-slate-500 hidden lg:inline">{services.length} Angebote</span>
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full px-3 h-9">
              <ArrowDownUp className="w-4 h-4 text-slate-400" />
              <select data-testid="sort-select" value={sort} onChange={(e) => onSort(e.target.value)} className="bg-transparent text-sm font-semibold text-slate-600 focus:outline-none">
                <option value="recommended">Empfohlen</option>
                <option value="price_asc">Preis aufsteigend</option>
                <option value="price_desc">Preis absteigend</option>
                <option value="rating">Beste Bewertung</option>
                <option value="distance">Entfernung</option>
              </select>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-1.5 rounded-full" data-testid="date-filter-btn">
                  <CalendarDays className="w-4 h-4" /> {date || "Termin"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar mode="single" selected={date ? new Date(date) : undefined}
                  onSelect={(d) => setDate(d ? toISO(d) : "")}
                  disabled={(x) => { const t = new Date(); t.setHours(0, 0, 0, 0); return x < t; }} />
                {date && <div className="p-2 border-t border-slate-100"><Button variant="ghost" size="sm" className="w-full" onClick={() => setDate("")} data-testid="date-clear">Termin entfernen</Button></div>}
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-1.5 rounded-full" data-testid="price-filter-btn">
                  <Euro className="w-4 h-4" /> {price[0] === 0 && price[1] === 2000 ? "Budget" : `${price[0]}–${price[1]} €`}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72" align="end">
                <div className="text-sm font-semibold text-slate-800 mb-4">Budget pro Angebot</div>
                <Slider data-testid="price-slider" value={priceDraft} min={0} max={2000} step={100} onValueChange={setPriceDraft} />
                <div className="flex justify-between text-xs font-medium text-slate-600 mt-2">
                  <span>{priceDraft[0]} €</span><span>{priceDraft[1] === 2000 ? "2000+ €" : `${priceDraft[1]} €`}</span>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => { setPriceDraft([0, 2000]); setPrice([0, 2000]); }} data-testid="price-reset">Zurücksetzen</Button>
                  <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => setPrice(priceDraft)} data-testid="price-apply">Anwenden</Button>
                </div>
              </PopoverContent>
            </Popover>
            <div className="flex bg-white border border-slate-200 rounded-full p-1">
              <button onClick={() => setView("list")} data-testid="view-list" className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === "list" ? "bg-emerald-600 text-white" : "text-slate-600"}`}><List className="w-4 h-4" /> Liste</button>
              <button onClick={() => setView("map")} data-testid="view-map" className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === "map" ? "bg-emerald-600 text-white" : "text-slate-600"}`}><MapIcon className="w-4 h-4" /> Karte</button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
        ) : services.length === 0 ? (
          <div className="text-center py-24 text-slate-500" data-testid="no-results">Keine Angebote gefunden. Versuche eine andere Suche.</div>
        ) : view === "map" ? (
          <div className="pb-8"><OffersMap services={displayed} /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-8" data-testid="services-grid">
            {displayed.map((s) => <ServiceCard key={s.id} service={s} />)}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
